"use client";

import { useEffect, useRef, useState } from "react";
import { ChevronDown, Globe } from "lucide-react";
import type { LanguageOption } from "../../types";
import { useLocale } from "../../runtime/RendererRuntime";
import { t } from "../../i18n/strings";

/** Props for the LanguageSelector component. */
export interface LanguageSelectorProps {
  language: string;
  languageOptions: LanguageOption[];
  currentLanguage: LanguageOption | undefined;
  onLanguageChange: (code: string) => void;
  /** Visual variant: "desktop" renders a text button, "mobile" renders a pill-shaped button. */
  variant: "desktop" | "mobile";
}

/** Reusable language selector dropdown. */
export function LanguageSelector({
  language,
  languageOptions,
  currentLanguage,
  onLanguageChange,
  variant,
}: LanguageSelectorProps) {
  const menuRef = useRef<HTMLDivElement | null>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const locale = useLocale();

  // Une seule langue active : pas de choix à offrir, le sélecteur disparaît
  // entièrement (bureau comme mobile) au lieu d'afficher un menu à une entrée.
  const singleLanguage = languageOptions.length <= 1;

  // Standard light-dismiss for the dropdown: any click outside the component or
  // an Escape press closes it. Registered once on document — the ref check is
  // what scopes it — so opening several menus never stacks listeners.
  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      const target = e.target as Node;
      if (menuRef.current && target && !menuRef.current.contains(target)) {
        setMenuOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setMenuOpen(false);
    };
    document.addEventListener("click", onClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("click", onClick);
      document.removeEventListener("keydown", onKey);
    };
  }, []);

  const handleChange = (code: string) => {
    onLanguageChange(code);
    setMenuOpen(false);
  };

  // Après les hooks (règle des hooks React) : rien à rendre s'il n'y a pas de choix.
  if (singleLanguage) return null;

  if (variant === "mobile") {
    return (
      <div ref={menuRef} className="relative">
        <button
          type="button"
          className="inline-flex items-center gap-2 rounded-full bg-white px-4 py-2 text-[15px] xl:text-[17px] font-semibold text-[#005CA9] border border-[#005CA9]/15 transition-colors hover:bg-[#EAF2F9]"
          aria-haspopup="listbox"
          aria-expanded={menuOpen}
          aria-label={t(locale, "changeLanguage")}
          onClick={() => setMenuOpen((prev) => !prev)}
        >
          <Globe className="h-4 w-4" aria-hidden="true" />
          <span className="flex items-center gap-2 leading-none">
            {currentLanguage?.label ?? "Français"}
            {currentLanguage?.flagIso && <span className={`fi fi-${currentLanguage.flagIso} text-[13px] xl:text-[15px]`}></span>}
          </span>
          <ChevronDown className={`h-4 w-4 transition-transform ${menuOpen ? "rotate-180" : ""}`} aria-hidden="true" />
        </button>
        {menuOpen && (
          <div className="absolute right-0 z-50 mt-2 w-48 rounded-lg border border-[#005CA9]/15 bg-white text-[#005CA9] shadow-xl">
            <ul role="listbox" aria-label={t(locale, "availableLanguages")} className="py-2">
              {languageOptions.map((option) => (
                <li key={option.code}>
                  <button
                    type="button"
                    role="option"
                    aria-selected={option.code === language}
                    className={`flex w-full items-center justify-between px-4 py-2 text-sm xl:text-base transition hover:bg-[#EAF2F9] ${option.code === language ? "font-semibold" : ""}`}
                    onClick={() => handleChange(option.code)}
                  >
                    <span className="flex items-center gap-2">
                      {option.label}
                      {option.flagIso && <span className={`fi fi-${option.flagIso}`}></span>}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    );
  }

  // Desktop variant. 765px : même seuil que la bascule de l'en-tête (SiteHeader), sinon le
  // sélecteur bureau réapparaît alors que la nav est encore en mode mobile.
  return (
    <div ref={menuRef} className="relative hidden min-[765px]:block">
      <button
        type="button"
        className="flex items-center gap-2 text-base xl:text-lg font-semibold capitalize text-[#005CA9] hover:opacity-80"
        style={{ fontFamily: "var(--font-nunito)" }}
        aria-haspopup="listbox"
        aria-expanded={menuOpen}
        onClick={() => setMenuOpen((prev) => !prev)}
      >
        <Globe className="h-4 w-4 text-[#0095DB]" aria-hidden="true" />
        <span>{currentLanguage?.label ?? "Français"}</span>
        <ChevronDown
          className={`h-4 w-4 text-[#FFA401] transition-transform ${menuOpen ? "rotate-180" : ""}`}
          aria-hidden="true"
        />
      </button>
      {menuOpen && (
        <div className="absolute right-0 z-50 mt-2 w-48 rounded-md border border-slate-200 bg-white py-1 shadow-lg">
          <ul role="listbox" aria-label={t(locale, "availableLanguages")}>
            {languageOptions.map((option) => (
              <li key={option.code}>
                <button
                  type="button"
                  role="option"
                  aria-selected={option.code === language}
                  className={`flex w-full items-center justify-between rounded px-3 py-2 text-sm xl:text-base transition hover:bg-slate-100 ${option.code === language ? "bg-[#0a4a8a]/10 text-[#0a4a8a]" : "text-[#0F2849]"}`}
                  onClick={() => handleChange(option.code)}
                >
                  <span className="flex items-center gap-2">
                    {option.flagIso && <span className={`fi fi-${option.flagIso} text-[13px] xl:text-[15px]`}></span>}
                    {option.label}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
